import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { WebPageJsonLD } from "@/components/feature/JsonLD";
import { categories, getCategoryBySlug } from "@/mocks/categories";
import { getArticlesByCategory } from "@/mocks/articles";
import NotFound from "@/pages/NotFound";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? getCategoryBySlug(slug) : null;

  if (!category) {
    return <NotFound />;
  }

  const articles = getArticlesByCategory(category.slug);

  return (
    <>
      <ScrollToTop />
      <WebPageJsonLD
        title={`${category.name} - 福利导航`}
        description={category.description}
        url={`https://fulidaohang.com/category/${category.slug}`}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Breadcrumb */}
        <div className="pt-20">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                { label: "分类浏览", path: "/category/gouwu-youhui" },
                { label: category.name },
              ]}
            />
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8">
            {/* Sidebar */}
            <aside className="w-full lg:w-60 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">
                  福利分类
                </h3>
                <nav className="flex flex-col gap-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors whitespace-nowrap ${
                        cat.slug === category.slug
                          ? "bg-primary-100 text-primary-700 font-medium"
                          : "text-foreground-600 hover:bg-background-100 hover:text-foreground-900"
                      }`}
                    >
                      <i className={`${cat.icon} text-base`}></i>
                      <span>{cat.name}</span>
                      <span className="ml-auto text-xs text-foreground-400">
                        {cat.articleCount}
                      </span>
                    </Link>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 min-w-0">
              <div className="mb-8">
                <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-3">
                  {category.name}
                </h1>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                  {category.description}
                </p>
              </div>

              {articles.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">
                    该分类暂无文章，敬请期待
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {articles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all"
                    >
                      <div className="relative w-full h-44 overflow-hidden">
                        <img
                          src={article.imageUrl}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-xs text-foreground-500 flex items-center gap-1">
                            <i className="ri-calendar-line"></i>
                            {article.publishedAt}
                          </span>
                          <span className="text-xs text-foreground-500 flex items-center gap-1">
                            <i className="ri-time-line"></i>
                            {article.readTime}
                          </span>
                        </div>
                        <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2">
                          {article.excerpt}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </main>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}